import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type WheelEvent } from "react";

import { DEFAULT_CROP, cssFilter, loadImage, place, type ImageKind } from "../lib/image";
import type { Crop } from "../lib/types";
import { Icon } from "./Icon";

const MAX_ZOOM = 5;

/**
 * Full-screen photo / background editor. Drag to move, pinch / wheel / slider to
 * zoom, rotate in 90° steps, and adjust brightness, contrast and saturation.
 * The frame shows exactly what will be saved: a circle for the photo, a 4:1
 * band for the background.
 */
export function ImageEditor({ kind, src, initial, onCancel, onSave }: {
  kind: ImageKind;
  src: string;
  initial?: Crop | null;
  onCancel: () => void;
  onSave: (crop: Crop, img: HTMLImageElement) => Promise<void>;
}) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({ ...DEFAULT_CROP, ...(initial ?? {}) });
  const [tab, setTab] = useState<"crop" | "adjust">("crop");
  const [busy, setBusy] = useState(false);
  const [frame, setFrame] = useState({ w: 0, h: 0 });
  const stage = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const title = kind === "avatar" ? "Edit photo" : "Edit background";

  useEffect(() => {
    loadImage(src).then(setImg, (e) => setError(e.message));
  }, [src]);

  // Frame size follows the stage: square for the photo, 4:1 for the background.
  useLayoutEffect(() => {
    const el = stage.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (kind === "avatar") {
        const side = Math.max(120, Math.min(width - 48, height - 48, 420));
        setFrame({ w: side, h: side });
      } else {
        const w = Math.max(200, Math.min(width - 32, 960));
        setFrame({ w, h: w / 4 });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [kind]);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const update = (fn: (c: Crop) => Crop) =>
    setCrop((c) => {
      const next = fn(c);
      next.zoom = Math.max(1, Math.min(MAX_ZOOM, next.zoom));
      return img && frame.w ? place(frame.w, frame.h, img.naturalWidth, img.naturalHeight, next).clamped : next;
    });
  const pan = (dx: number, dy: number) => update((c) => ({ ...c, x: c.x + dx / frame.w, y: c.y + dy / frame.h }));

  const spread = () => {
    const [a, b] = [...pointers.current.values()];
    return { d: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
  };
  const onPointerDown = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  };
  const onPointerMove = (e: PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    if (pointers.current.size === 2) {
      const before = spread();
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const after = spread();
      update((c) => ({ ...c, zoom: c.zoom * (after.d / (before.d || 1)), x: c.x + (after.mx - before.mx) / frame.w, y: c.y + (after.my - before.my) / frame.h }));
    } else {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      pan(e.clientX - prev.x, e.clientY - prev.y);
    }
  };
  const onPointerUp = (e: PointerEvent) => pointers.current.delete(e.pointerId);
  const onWheel = (e: WheelEvent) => update((c) => ({ ...c, zoom: c.zoom * Math.exp(-e.deltaY * 0.0015) }));
  const onKeyDown = (e: KeyboardEvent) => {
    const step = e.shiftKey ? 20 : 4;
    const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (moves[e.key]) {
      e.preventDefault();
      pan(...moves[e.key]);
    } else if (e.key === "+" || e.key === "=") update((c) => ({ ...c, zoom: c.zoom * 1.1 }));
    else if (e.key === "-") update((c) => ({ ...c, zoom: c.zoom / 1.1 }));
  };

  const save = async () => {
    if (!img) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(place(frame.w, frame.h, img.naturalWidth, img.naturalHeight, crop).clamped, img);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const p = img && frame.w ? place(frame.w, frame.h, img.naturalWidth, img.naturalHeight, crop) : null;

  return (
    <div className="editor" role="dialog" aria-modal="true" aria-label={title}>
      <header className="editor-bar">
        <button className="link-btn" onClick={onCancel} disabled={busy}>Cancel</button>
        <span className="d" style={{ fontWeight: 700 }}>{title}</span>
        <button className="link-btn b" onClick={save} disabled={!img || busy}>{busy ? "Saving…" : "Save"}</button>
      </header>
      <div
        ref={stage}
        className="editor-stage"
        tabIndex={0}
        aria-label="Drag to move, pinch or scroll to zoom. Arrow keys move, plus and minus zoom."
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onKeyDown={onKeyDown}
      >
        <div className={`editor-frame ${kind}`} style={{ width: frame.w, height: frame.h }}>
          {img && p && (
            <img
              src={src}
              alt=""
              draggable={false}
              style={{
                width: p.w,
                height: p.h,
                transform: `translate(-50%, -50%) translate(${p.ox}px, ${p.oy}px) rotate(${crop.rotation}deg)`,
                filter: cssFilter(crop),
              }}
            />
          )}
          <span className="editor-mask" />
        </div>
        {!img && !error && <p className="editor-note">Loading…</p>}
      </div>
      <div className="editor-tools">
        {error && <p className="error small" role="alert">{error}</p>}
        <div className="chips" role="tablist" aria-label="Tools">
          <button role="tab" aria-selected={tab === "crop"} className={`chip${tab === "crop" ? " on" : ""}`} onClick={() => setTab("crop")}>Crop</button>
          <button role="tab" aria-selected={tab === "adjust"} className={`chip${tab === "adjust" ? " on" : ""}`} onClick={() => setTab("adjust")}>Adjust</button>
        </div>
        {tab === "crop" ? (
          <>
            <div className="row">
              <Icon name="minus" size={18} />
              <input className="range grow" type="range" min={1} max={MAX_ZOOM} step={0.01} value={crop.zoom} aria-label="Zoom"
                onChange={(e) => update((c) => ({ ...c, zoom: Number(e.target.value) }))} />
              <Icon name="plus" size={18} />
            </div>
            <div className="grid-2">
              <button className="btn" onClick={() => update((c) => ({ ...c, rotation: ((c.rotation + 90) % 360) as Crop["rotation"] }))}><Icon name="rotate" size={20} />Rotate</button>
              <button className="btn" onClick={() => update((c) => ({ ...DEFAULT_CROP, brightness: c.brightness, contrast: c.contrast, saturation: c.saturation }))}>Reset</button>
            </div>
            <p className="xs muted center">Drag to move · pinch or scroll to zoom</p>
          </>
        ) : (
          <>
            <Slider label="Brightness" min={0.5} max={1.5} value={crop.brightness} onChange={(v) => setCrop({ ...crop, brightness: v })} />
            <Slider label="Contrast" min={0.5} max={1.5} value={crop.contrast} onChange={(v) => setCrop({ ...crop, contrast: v })} />
            <Slider label="Saturation" min={0} max={2} value={crop.saturation} onChange={(v) => setCrop({ ...crop, saturation: v })} />
            <button className="btn" onClick={() => setCrop({ ...crop, brightness: 1, contrast: 1, saturation: 1 })}>Reset adjustments</button>
          </>
        )}
      </div>
    </div>
  );
}

function Slider({ label, min, max, value, onChange }: { label: string; min: number; max: number; value: number; onChange: (v: number) => void }) {
  return (
    <label className="field">
      <span className="row between"><span className="field-label">{label}</span><span className="m xs muted">{Math.round(value * 100)}%</span></span>
      <input className="range" type="range" min={min} max={max} step={0.01} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );
}
